<template>
    <div v-if="!hasSubsGodTrialAccess" class="mt-[42px]">
        <div class="flex mt-[42px] mb-[42px] justify-between h-[42px]">
            <h1
                class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
            >
                {{ $t("vue.account_members") }}
            </h1>
        </div>

        <InfoCard
            :img-src="lockedNotAuthorized"
            img-alt="User has no access to content"
            :title="t('vue.upgrade_required')"
            :body-text="t('vue.not_having_access_message')"
            :show-dot="false"
            :disable-top-margin="false"
            :button-label="t('vue.upgrade_my_plan')"
            :button-href="route('billing.subscriptions.index')"
        />
    </div>

    <MemberTabs v-else class="mt-[42px]" tab-key="members">
        <div class="mt-[42px] pb-80">
            <DynamicContentTable
                class="mt-10 cursor-default"
                :title="`${$t('vue.your_teams_active_members')} (${memberLength})`"
                :subtitle="$t('vue.list_of_all_people_with_access_to_acc')"
                :search-enabled="false"
                :rows="membersRows"
                :columns="memberColumns"
                :endpoint="() => route('members.index')"
                :base-query="{}"
                :table="singlePageTable(memberLength)"
                :pagination="singlePagePagination(memberLength)"
                :pagination-enabled="false"
                empty-text="No results"
                :per-page-options="[]"
                per-page-label=""
                :row-key="(r: any) => r.id"
            >
                <template #row="{ row: member }">
                    <!-- Name -->
                    <td class="px-6 py-4">
                        <div class="flex items-center gap-3">
                            <div
                                class="h-8 w-8 rounded-full ring-[1px] ring-gray-200 overflow-hidden bg-gray-100"
                            >
                                <img
                                    v-if="member.profile_photo_url"
                                    :src="member.profile_photo_url"
                                    class="h-8 w-8 object-cover"
                                    alt="Member avatar"
                                />
                            </div>
                            <span class="text-sm font-medium text-gray-700">
                                {{ member.name }}
                            </span>
                        </div>
                    </td>

                    <!-- Email -->
                    <td class="px-6 py-4">
                        <span class="text-sm text-gray-500">{{
                            member.email
                        }}</span>
                    </td>

                    <!-- Joined -->
                    <td class="px-6 py-4">
                        <span class="text-sm text-gray-500">
                            {{
                                format(
                                    new Date(member.created_at),
                                    "d. L. yyyy",
                                )
                            }}
                        </span>
                    </td>

                    <!-- Permissions / Role -->
                    <td class="px-6 py-4">
                        <TableMembersDropdown
                            v-if="
                                (isOwner || isAdmin) && member.role !== 'owner'
                            "
                            :selected="selectedRole(member.role)"
                            :items="list"
                            @select="updateRole"
                            @click="selectMember(member)"
                        />

                        <div v-else>
                            <button
                                type="button"
                                class="flex h-10 min-w-[160px] items-center justify-between rounded-md border border-gray-300 bg-white px-2 py-3 text-sm font-medium text-gray-600 transition duration-100 ease-in-out hover:text-gray-800 hover:shadow-button focus:text-gray-800 focus:outline-hidden focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-white"
                            >
                                <p>
                                    {{
                                        member.role.charAt(0).toUpperCase() +
                                        member.role.slice(1)
                                    }}
                                </p>
                            </button>
                        </div>
                    </td>

                    <!-- ACTIONS -->
                    <td
                        class="pl-2 pr-6 py-4 whitespace-nowrap text-right"
                        @click.stop
                    >
                        <div class="flex justify-end">
                            <template v-if="canDeleteMember(member)">
                                <button
                                    :ref="
                                        (el) =>
                                            setKebabBtn(
                                                member.id,
                                                el as HTMLElement | null,
                                            )
                                    "
                                    class="kebab-btn"
                                    aria-haspopup="menu"
                                    :aria-expanded="
                                        menuOpenId === member.id
                                            ? 'true'
                                            : 'false'
                                    "
                                    aria-label="Open actions"
                                    @click="toggleMenu(member.id)"
                                >
                                    <span
                                        class="kebab-dots"
                                        aria-hidden="true"
                                    ></span>
                                </button>

                                <Teleport to="body">
                                    <transition name="fade">
                                        <div
                                            v-if="menuOpenId === member.id"
                                            :ref="
                                                (el) =>
                                                    setMenuEl(
                                                        member.id,
                                                        el as HTMLElement | null,
                                                    )
                                            "
                                            class="menu-popover"
                                            :style="menuStyle"
                                            role="menu"
                                            @keydown.esc.stop.prevent="
                                                closeMenu
                                            "
                                        >
                                            <button
                                                class="menu-item danger"
                                                role="menuitem"
                                                @click="onDeleteMember(member)"
                                            >
                                                {{ $t("vue.delete") }}
                                            </button>
                                        </div>
                                    </transition>
                                </Teleport>
                            </template>
                        </div>
                    </td>
                </template>
            </DynamicContentTable>

            <RemoveMemberModal
                v-if="removeMemberModal"
                :member="selectedMember"
                :team="teamProps"
                @keydown.esc="removeMemberModal = false"
                @remove="removeMember"
                @close="removeMemberModal = false"
            />
        </div>
    </MemberTabs>
</template>

<script lang="ts" setup>
import { format } from "date-fns";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import type IUser from "@/types/Members/IUser";
import { create } from "@composables/useNotifications";
import type IPaginationData from "@/components/Atomic/DataTable/types/IPaginationData";
import type IDataTableHeader from "@/components/Atomic/DataTable/types/IDataTableHeader";

import usePermissions from "@/composables/usePermissions";
import { usePage } from "@inertiajs/vue3";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import type { IDataTableFilter } from "@/components/Atomic/DataTable/types/IDataTableFilter";
import { useTranslations } from "@/composables/useTranslations";

import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import useSubsGodTrialAccess from "@/composables/useSubsGodTrialAccess.ts";

import { computed, ref } from "vue";

const props = defineProps<{
    members: IPaginationData<IUser>;
}>();

const selectedMember = ref<IUser>();
const { isAdmin, isOwner } = usePermissions();
const { t } = useTranslations();
const removeMemberModal = ref(false);
const globalProps = computed(() => usePage<IRhizomPageProps>().props);

const teamProps = computed(() => globalProps.value.team as ICurrentTeam);

const memberLength = computed(() => props.members.data.length);
const { getCurrentTeamMembersCount } = useTeamsAndMembers();

const lockedNotAuthorized = new URL(
    "/resources/js/assets/images/locked-not-authorized.svg",
    import.meta.url,
).href;

/*TABLE DEF START*/
const membersRows = computed(() => props.members.data);

const canDeleteMember = (member: IUser) =>
    isOwner.value && member.role !== "owner";

const memberColumns = [
    { key: "name", label: t("vue.name"), colClass: "w-[32%]", sortable: false },
    {
        key: "email",
        label: t("vue.email"),
        colClass: "w-[28%]",
        sortable: false,
    },
    {
        key: "joined",
        label: t("vue.joined"),
        colClass: "w-[20%]",
        sortable: false,
    },
    {
        key: "role",
        label: t("vue.permissions_role"),
        colClass: "w-[16%]",
        sortable: false,
    },
    { key: "actions", label: "", colClass: "w-[72px]", sortable: false },
];

type SortDir = "asc" | "desc";

const singlePageTable = (total: number) => ({
    sort_by: null,
    sort_dir: null as SortDir | null,
    per_page: total || 1,
    current_page: 1,
    q: "",
});

const singlePagePagination = (total: number) => ({
    total,
    per_page: total || 1,
    current_page: 1,
    last_page: 1,
    from: total ? 1 : null,
    to: total ? total : null,
});
/*TABLE DEF END*/

const headers: IDataTableHeader[] = [
    { text: "", id: "profile_photo_url" },
    {
        text: t("vue.name"),
        id: "name",
        sortable: true,
        sortableId: "name",
    },
    {
        text: t("vue.email"),
        id: "email",
        sortable: true,
        sortableId: "email",
    },
    {
        text: t("vue.joined"),
        id: "created_at",
        sortable: true,
        sortableId: "team_user.created_at",
    },
    {
        text: t("vue.permissions_role"),
        id: "role",
        sortable: true,
        sortableId: "role",
    },
    { text: "", id: "custom" },
];

const filters: IDataTableFilter[] = [
    {
        column: "role",
        label: "Member role",
        values: [
            {
                label: "Owners",
                value: "owner",
            },
            {
                label: "Admins",
                value: "admin",
            },
            {
                label: "Editors",
                value: "editor",
            },
            {
                label: "Viewers",
                value: "viewer",
            },
        ],
    },
];

const list = [
    // {
    //     title: 'Team owner',
    //     subtitle:
    //         'Only team owner can transfer team ownership to other team member.',
    //     value: 'owner',
    // },
    {
        title: "Administrator",
        subtitle:
            "Can invite or remove team members and change member’s rights.",
        value: "admin",
    },
    {
        title: "Editor",
        subtitle:
            "Can perform all actions but can’t invite or remove team members or change their rights.",
        value: "editor",
    },
    {
        title: "Viewer",
        subtitle: "Can only view team.",
        value: "viewer",
    },
];

const selectedRole = (value: string) => {
    const a = list.filter((a) => a.value === value);
    const b = Object.assign({}, ...a);
    return b;
};

const selectMember = (value: IUser) => {
    selectedMember.value = value;
};

const updateRole = (value: string) => {
    const a = list.find((a) => a.value === value);
    if (a && selectedMember.value) {
        router.put(
            route("members.update", {
                member: selectedMember.value.id,
            }),
            {
                role: a.value,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: "Team member updated",
                        type: "success",
                    });
                },
                onError: () => {
                    create({
                        title: "Team member not updated",
                        text: "There was an error updating your team member.",
                        type: "error",
                    });
                },
            },
        );
    }
};
const removeMemberModalShow = (member: IUser) => {
    selectedMember.value = member;
    removeMemberModal.value = true;
};
const removeMember = async () => {
    if (selectedMember.value) {
        router.delete(
            route("members.delete", {
                member: selectedMember.value.id,
            }),
            {
                preserveScroll: true,
                onSuccess: () => {
                    getCurrentTeamMembersCount();
                    create({
                        title: "Team member removed",
                        type: "success",
                    });
                },
                onError: () => {
                    create({
                        title: "Team member not removed",
                        text: "There was an error removing your team member.",
                        type: "error",
                    });
                },
                onFinish: () => {
                    removeMemberModal.value = false;
                },
            },
        );
    }
};

const { hasSubsGodTrialAccess } = useSubsGodTrialAccess();

// TODO: implement as composable!!!
// KEBAB MENU
import { onMounted, onBeforeUnmount, nextTick } from "vue";

const menuOpenId = ref<number | string | null>(null);

const kebabBtns = ref<Record<string, HTMLElement | null>>({});
const menus = ref<Record<string, HTMLElement | null>>({});

const setKebabBtn = (id: number | string, el: HTMLElement | null) => {
    kebabBtns.value[String(id)] = el;
};

const setMenuEl = (id: number | string, el: HTMLElement | null) => {
    menus.value[String(id)] = el;
};

const menuPos = ref({ top: 0, left: 0 });

const updateMenuPosition = () => {
    if (!menuOpenId.value) return;

    const id = String(menuOpenId.value);
    const btn = kebabBtns.value[id];
    const menu = menus.value[id];
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const padding = 8;

    const menuW = menu?.offsetWidth ?? 176;
    const menuH = menu?.offsetHeight ?? 0;

    let top = rect.bottom + 8;
    let left = rect.right - menuW;

    left = Math.min(
        Math.max(left, padding),
        window.innerWidth - menuW - padding,
    );

    if (menuH && top + menuH > window.innerHeight - padding) {
        top = Math.max(padding, rect.top - menuH - 8);
    }

    menuPos.value = { top, left };
};

const menuStyle = computed(() => ({
    position: "fixed",
    top: `${menuPos.value.top}px`,
    left: `${menuPos.value.left}px`,
    zIndex: "99999",
}));

const toggleMenu = async (id: number | string) => {
    menuOpenId.value = menuOpenId.value === id ? null : id;

    if (menuOpenId.value) {
        await nextTick();
        requestAnimationFrame(() => updateMenuPosition());
    }
};

const closeMenu = () => {
    menuOpenId.value = null;
};

const onClickOutside = (e: MouseEvent) => {
    if (!menuOpenId.value) return;

    const id = String(menuOpenId.value);
    const menu = menus.value[id];
    const btn = kebabBtns.value[id];
    const target = e.target as Node;

    if (menu && !menu.contains(target) && btn && !btn.contains(target)) {
        closeMenu();
    }
};

const onScrollOrResize = () => {
    if (!menuOpenId.value) return;
    updateMenuPosition();
};

onMounted(() => {
    document.addEventListener("click", onClickOutside);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", onClickOutside);
    window.removeEventListener("resize", onScrollOrResize);
    window.removeEventListener("scroll", onScrollOrResize, true);
});

const onDeleteMember = (member: IUser) => {
    closeMenu();
    removeMemberModalShow(member);
};
</script>

<style scoped>
/*TODO: this will be moved to specific component in parallel to kebab composable*/
.kebab-btn {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    border-radius: 8px;
}
.kebab-btn:hover {
    background: #f3f4f6;
}

.kebab-dots {
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: #6b7280;
    position: relative;
}
.kebab-dots::before,
.kebab-dots::after {
    content: "";
    position: absolute;
    left: 0;
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: #6b7280;
}
.kebab-dots::before {
    top: -7px;
}
.kebab-dots::after {
    top: 7px;
}

.menu-popover {
    z-index: 99999;
    width: 176px;
    border: 1px solid #e5e7eb;
    background: #fff;
    border-radius: 8px;
    box-shadow:
        0 8px 24px rgba(16, 24, 40, 0.12),
        0 2px 8px rgba(16, 24, 40, 0.06);
    padding: 6px 0;
}

.menu-item {
    display: block;
    width: 100%;
    text-align: left;
    padding: 8px 12px;
    font-size: 14px;
    line-height: 20px;
    color: #374151;
}
.menu-item:hover {
    background: #f9fafb;
}

.menu-item.danger {
    color: #b91c1c;
}
.menu-item.danger:hover {
    background: #fef2f2;
    cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.12s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
